"use client"

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Button, Form, InputGroup, ListGroup, Modal, Spinner, Card, Alert } from "react-bootstrap"
import {
  Send,
  Plus,
  Users,
  AlertCircle,
  MessageCircle,
  Clock,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ArrowDown,
  Paperclip,
  X,
  Loader2,
  Link2,
  Smile,
} from "lucide-react"
import signalRService from "../../Services/signalRService"
import useChats from "../../hooks/DataFetching/useChats"
import "../../styles/components/GroupForums.css"
import { useDispatch, useSelector } from "react-redux"
import { setSelectedForumTopic } from "../../redux/actions/topicActions"
import { addPost, deletePost, setError } from "../../redux/actions/chatActions"
import { useHistory, useLocation } from "react-router-dom"

const TOPIC_CATEGORIES = [
  { value: "Welcome and Orientation", color: "#6366f1" },
  { value: "Problems and Needs", color: "#dc2626" },
  { value: "Ideas and Solutions", color: "#16a34a" },
  { value: "Vision and Direction", color: "#8b5cf6" },
  { value: "Features and Technical Roadmap", color: "#0ea5e9" },
  { value: "Bug Reporting", color: "#f59e0b" },
  { value: "Prayers and Support", color: "#10b981" },
  { value: "Miracles and Blessings Reports", color: "#f472b6" },
]

const UNCATEGORIZED_CATEGORY = "Uncategorized"
const ALL_CATEGORIES_TAB = "All"
const TOPICS_PAGE_SIZE = 30
const CATEGORY_TABS = [ALL_CATEGORIES_TAB, ...TOPIC_CATEGORIES.map(({ value }) => value), UNCATEGORIZED_CATEGORY]
const EMOJI_OPTIONS = [
  "😂",
  "❤️",
  "👍",
  "😭",
  "🙏",
  "😍",
  "😊",
  "🔥",
  "🤣",
  "😎",
  "😢",
  "🎉",
  "💯",
  "🤔",
  "😅",
  "🙌",
  "👏",
  "😬",
  "😌",
  "🥰",
  "✅",
  "✨",
  "🤩",
  "🤷",
]

const normalizeTopicIdValue = (value) => {
  if (value === null || value === undefined) return null
  const stringValue = String(value)
  return stringValue.toLowerCase() === "null" ? null : stringValue
}

export function GroupForums({ groupId, forumTitle = "Group Forums", isGlobalForum = false }) {
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0()
  const location = useLocation()
  const history = useHistory()
  const [newMessage, setNewMessage] = useState("")
  const [showNewTopicModal, setShowNewTopicModal] = useState(false)
  const [newTopicTitle, setNewTopicTitle] = useState("")
  const [newTopicCategory, setNewTopicCategory] = useState(TOPIC_CATEGORIES[0].value)
  const [error, setErrorState] = useState(null)
  const [isSignalRConnected, setIsSignalRConnected] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [messageToDelete, setMessageToDelete] = useState(null)
  const [participants, setParticipants] = useState({})
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [forumTopics, setForumTopics] = useState([])
  const [isTopicsCollapsed, setIsTopicsCollapsed] = useState(false)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  const [expandedThreads, setExpandedThreads] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_TAB)
  const [isMobile, setIsMobile] = useState(false)
  const [pendingTopicId, setPendingTopicId] = useState(null)
  const [pendingPostId, setPendingPostId] = useState(null)
  const [pendingView, setPendingView] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageName, setSelectedImageName] = useState("")
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(true)
  const [copiedTopicId, setCopiedTopicId] = useState(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  // ✅ MINIMAL CHANGE: declare loading BEFORE fetchForumTopics uses setLoading
  const [loading, setLoading] = useState(true)

  const messagesEndRef = useRef(null)
  const chatMessagesRef = useRef(null)
  const messageRefs = useRef({})
  const fileInputRef = useRef(null)
  const messageInputRef = useRef(null)

  const dispatch = useDispatch()
  const { posts, fetchPosts, sendMessage, setPosts } = useChats() // Pass groupId to useChats
  const selectedTopic = useSelector((state) => state.topic?.selectedForumTopic)
  const isGroupForum = !isGlobalForum
  const isGuideTopic = selectedTopic?.id === "socratic-guide" || selectedTopic?.isGuide
  const activeTopic = useMemo(() => {
    if (!selectedTopic || isGuideTopic) return null
    const normalizedSelectedId = normalizeTopicIdValue(selectedTopic.id)
    return forumTopics.find((topic) => normalizeTopicIdValue(topic.id) === normalizedSelectedId) ?? selectedTopic
  }, [forumTopics, isGuideTopic, selectedTopic])
  const hasActiveTopic = Boolean(activeTopic)

  // ✅ MINIMAL CHANGE: normalize posts so map/find/length never crash
  const postsArray = useMemo(() => {
    if (Array.isArray(posts)) return posts
    return posts?.$values ?? posts?.items ?? posts?.posts ?? []
  }, [posts])

  useEffect(() => {
    const initialLikes = {}
    postsArray.forEach((post) => {
      if (post?.likedByCurrentUser) {
        initialLikes[post.id] = true
      }
    })
    setLikedPosts(initialLikes)
  }, [postsArray])

  const visiblePosts = useMemo(() => postsArray.filter((post) => !post.isDeleted), [postsArray])

  const repliesByParent = useMemo(() => {
    const map = {}

    visiblePosts.forEach((post) => {
      if (!post?.atPostId) return
      if (!map[post.atPostId]) {
        map[post.atPostId] = []
      }
      map[post.atPostId].push(post)
    })

    Object.values(map).forEach((replyList) =>
      replyList.sort((a, b) => {
        const aDate = a?.postDate ? new Date(a.postDate).getTime() : a?.createdOn ? new Date(a.createdOn).getTime() : 0
        const bDate = b?.postDate ? new Date(b.postDate).getTime() : b?.createdOn ? new Date(b.createdOn).getTime() : 0
        return aDate - bDate
      }),
    )

    return map
  }, [visiblePosts])

  const rootPosts = useMemo(() => {
    const roots = visiblePosts.filter((post) => !post.atPostId || !visiblePosts.find((p) => p.id === post.atPostId))
    return [...roots].sort((a, b) => {
      const aDate = a?.postDate ? new Date(a.postDate).getTime() : a?.createdOn ? new Date(a.createdOn).getTime() : 0
      const bDate = b?.postDate ? new Date(b.postDate).getTime() : b?.createdOn ? new Date(b.createdOn).getTime() : 0
      return aDate - bDate
    })
  }, [visiblePosts])

  const registerMessageRef = useCallback((postId, node) => {
    if (node) {
      messageRefs.current[postId] = node
    } else {
      delete messageRefs.current[postId]
    }
  }, [])

  const highlightPost = useCallback((postId) => {
    const target = messageRefs.current[postId]
    if (target) {
      target.classList.add("notification-highlight")
      target.scrollIntoView({ behavior: "smooth", block: "center" })
      setTimeout(() => target.classList.remove("notification-highlight"), 2000)
    }
  }, [])

  const expandThreadChain = useCallback(
    (postId) => {
      const visited = new Set()
      const updates = {}
      let current = visiblePosts.find((p) => p.id === postId)

      while (current?.atPostId && !visited.has(current.atPostId)) {
        updates[current.atPostId] = true
        visited.add(current.atPostId)
        current = visiblePosts.find((p) => p.id === current.atPostId)
      }

      if (Object.keys(updates).length > 0) {
        setExpandedThreads((prev) => ({ ...prev, ...updates }))
      }
    },
    [visiblePosts],
  )

   const getTopicLastActivity = useCallback((topic) => {
    if (!topic) return 0

    const parseDateToTime = (value) => {
      if (!value) return null
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? null : time
    }

    const messageCollection = topic.messages ?? topic.Messages ?? []
    const messagesArray = Array.isArray(messageCollection) ? messageCollection : messageCollection?.$values ?? []
    const latestMessageTime = messagesArray.reduce((latest, message) => {
      const candidateTime = parseDateToTime(message?.postDate ?? message?.createdOn ?? message?.createdDate)
      if (candidateTime && (!latest || candidateTime > latest)) {
        return candidateTime
      }
      return latest
    }, null)

    const candidateTimes = [
      parseDateToTime(
        topic.latestPostDate ??
          topic.latestMessageDate ??
          topic.latestMessageOn ??
          topic.lastPostDate ??
          topic.lastPostOn ??
          topic.lastMessageDate ??
          topic.lastMessageOn,
      ),
      parseDateToTime(topic.updatedOn ?? topic.updatedAt ?? topic.modifiedOn ?? topic.modifiedAt),
      latestMessageTime,
      parseDateToTime(topic.createdOn ?? topic.createdAt),
    ].filter((time) => typeof time === "number")

    return candidateTimes.length > 0 ? Math.max(...candidateTimes) : 0
  }, [])

  const sortedTopics = useMemo(() => {
    return [...forumTopics].sort((a, b) => {
      const aDate = getTopicLastActivity(a)
      const bDate = getTopicLastActivity(b)
      return bDate - aDate
    })
  }, [forumTopics, getTopicLastActivity])

  const filteredTopics = useMemo(() => {
    if (isGroupForum) return sortedTopics
    if (selectedCategory === ALL_CATEGORIES_TAB) return sortedTopics
    if (selectedCategory === UNCATEGORIZED_CATEGORY) {
      return sortedTopics.filter(
        (topic) => !TOPIC_CATEGORIES.some(({ value }) => value === topic?.category) || !topic?.category,
      )
    }
    return sortedTopics.filter((topic) => topic?.category === selectedCategory)
  }, [isGroupForum, selectedCategory, sortedTopics])

  const displayedTopics = useMemo(
    () => (filteredTopics.length > TOPICS_PAGE_SIZE ? filteredTopics.slice(0, TOPICS_PAGE_SIZE) : filteredTopics),
    [filteredTopics],
  )

  const getCategoryDetails = useCallback((category) => {
    const matchingCategory = TOPIC_CATEGORIES.find(({ value }) => value === category)
    if (matchingCategory) {
      return matchingCategory
    }

    if (category) {
      return { value: category, color: "#6c757d" }
    }

    return { value: UNCATEGORIZED_CATEGORY, color: "#6c757d" }
  }, [])

  // Detect dark mode
  useEffect(() => {
    // Check if dark mode is enabled in the document
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains("dark") || document.body.classList.contains("dark")
      setIsDarkMode(isDark)
    }

    // Initial check
    checkDarkMode()

    // Set up a mutation observer to detect class changes on the document
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const view = params.get("view")
    setPendingView(view)

    if (view && view.toLowerCase() === "chat") {
      setPendingTopicId(null)
      setPendingPostId(null)
      return
    }

    const topicIdParam = params.get("topicId")
    const postIdParam = params.get("postId")

    setPendingTopicId(normalizeTopicIdValue(topicIdParam))
    setPendingPostId(postIdParam ? Number(postIdParam) : null)
  }, [location.search])

  const buildTopicShareUrl = useCallback(
    (topicId) => {
      const params = new URLSearchParams(location.search)
      if (topicId) {
        params.set("topicId", topicId)
      }
      params.delete("postId")
      params.delete("view")
      const search = params.toString()
      return `${window.location.origin}${location.pathname}${search ? `?${search}` : ""}`
    },
    [location.pathname, location.search],
  )

  const updateTopicUrl = useCallback(
    (topicId) => {
      const normalizedId = normalizeTopicIdValue(topicId)
      const params = new URLSearchParams(location.search)
      if (normalizedId) {
        params.set("topicId", normalizedId)
      } else {
        params.delete("topicId")
      }
      params.delete("postId")
      params.delete("view")
      const search = params.toString()
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : "" })
    },
    [history, location.pathname, location.search],
  )

  const copyTopicLink = useCallback(
    async (topicId) => {
      if (!topicId) return
      const shareUrl = buildTopicShareUrl(topicId)
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl)
        } else {
          throw new Error("Clipboard API unavailable")
        }
        setCopiedTopicId(topicId)
        setTimeout(() => setCopiedTopicId(null), 2000)
      } catch (err) {
        console.error("Clipboard write failed, falling back to prompt", err)
        try {
          window.prompt("Copy this topic link", shareUrl)
          setCopiedTopicId(topicId)
        } catch (promptError) {
          console.error("Unable to provide share link to the user", promptError)
        }
      }
    },
    [buildTopicShareUrl],
  )

  const fetchForumTopics = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const hasGroupScope = groupId !== undefined && groupId !== null
      const effectiveGroupId = hasGroupScope ? groupId : 0

      // Use the Topics controller for both group and community forums
      const endpoint =
        hasGroupScope || isGlobalForum
          ? `${process.env.REACT_APP_API_URL}/api/Topics/bygroup?groupId=${effectiveGroupId}&isChat=false`
          : `${process.env.REACT_APP_API_URL}/api/chats`

      const response = await fetch(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log("Forum topics data:", data) // Add this line to debug
        const rawTopics = Array.isArray(data) ? data : data?.$values || []
        const filteredTopics =
          hasGroupScope || isGlobalForum
            ? rawTopics.filter((topic) => {
                const topicFlag = topic?.isChat
                return (
                  topicFlag === false ||
                  topicFlag === "False" ||
                  topicFlag === "false" ||
                  topicFlag === 0 ||
                  topicFlag === undefined ||
                  topicFlag === null
                )
              })
            : rawTopics
        const normalizedTopics = filteredTopics.map((topic) => ({
          ...topic,
          id: normalizeTopicIdValue(
            topic.id ??
              topic.topicId ??
              topic.TopicId ??
              topic.chatId ??
              topic.ChatId ??
              topic.topicID ??
              topic.TopicID,
          ),
          category: topic.category ?? topic.Category ?? null,
        }))
        setForumTopics(normalizedTopics)
        setLoading(false)
        return normalizedTopics
      }

      console.error("Failed to fetch forum topics")
      setLoading(false)
      return []
    } catch (error) {
      console.error("Error fetching forum topics:", error)
      setLoading(false)
      return []
    }
  }, [isAuthenticated, getAccessTokenSilently, groupId, isGlobalForum])

  // Add this useEffect to fetch forum topics when the component mounts
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      setLoading(true)
      fetchForumTopics()
    }
  }, [fetchForumTopics, isAuthenticated, authLoading])

  useEffect(() => {
    if (!pendingTopicId || pendingView === "chat") return

    const targetTopic = forumTopics.find((topic) => normalizeTopicIdValue(topic.id) === normalizeTopicIdValue(pendingTopicId))
    if (targetTopic) {
      dispatch(setSelectedForumTopic(targetTopic))
      fetchPosts(targetTopic.id)
      setIsTopicsCollapsed(true)
    }
  }, [dispatch, fetchPosts, forumTopics, pendingTopicId, pendingView])

  useEffect(() => {
    if (!pendingPostId || pendingView === "chat") return
    if (
      pendingTopicId &&
      activeTopic?.id &&
      normalizeTopicIdValue(pendingTopicId) !== normalizeTopicIdValue(activeTopic.id)
    )
      return
    if (!activeTopic || visiblePosts.length === 0) return

    expandThreadChain(pendingPostId)
    const target = messageRefs.current[pendingPostId]
    if (!target) return

    requestAnimationFrame(() => highlightPost(pendingPostId))
    setPendingPostId(null)
  }, [
    expandThreadChain,
    highlightPost,
    pendingPostId,
    pendingTopicId,
    pendingView,
    activeTopic,
    expandedThreads,
    visiblePosts.length,
  ])

  // Handle scroll events in the messages container
  const handleMessagesScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target

    // Check if user has scrolled up
    if (scrollHeight - scrollTop - clientHeight > 100) {
      setUserHasScrolled(true)
      setShowScrollToBottom(true)
    } else {
      // User is at the bottom
      setUserHasScrolled(false)
      setShowScrollToBottom(false)
    }
  }, [])

  const scrollMessagesToBottom = useCallback((behavior = "smooth") => {
    const container = chatMessagesRef.current
    if (!container) return

    if (typeof container.scrollTo === "function") {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      })
    } else {
      container.scrollTop = container.scrollHeight
    }
  }, [])

  const insertEmoji = useCallback(
    (emoji) => {
      const input = messageInputRef.current
      if (input && typeof input.selectionStart === "number") {
        const start = input.selectionStart
        const end = input.selectionEnd
        const nextValue = `${newMessage.slice(0, start)}${emoji}${newMessage.slice(end)}`
        setNewMessage(nextValue)
        requestAnimationFrame(() => {
          input.focus()
          const cursor = start + emoji.length
          input.setSelectionRange(cursor, cursor)
        })
      } else {
        setNewMessage((prev) => `${prev}${emoji}`)
      }
    },
    [newMessage]
  )

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    scrollMessagesToBottom()
    setUserHasScrolled(false)
    setShowScrollToBottom(false)
  }, [scrollMessagesToBottom])

  // Auto-scroll to bottom of messages when posts change, but only if user hasn't scrolled up
  useEffect(() => {
    if (!userHasScrolled) {
      scrollMessagesToBottom()
    }
  }, [visiblePosts, userHasScrolled, scrollMessagesToBottom]) // ✅ MINIMAL CHANGE: posts -> postsArray

  // Simplified handleReceiveMessage function that always updates the current topic
  const handleReceiveMessage = useCallback(
    (newMessage) => {
      console.log("SignalR message received:", newMessage)

      // Always add the message to Redux store
      dispatch(addPost(newMessage))

      const matchesActiveTopic =
        activeTopic && normalizeTopicIdValue(activeTopic.id) === normalizeTopicIdValue(newMessage?.topicId)

      if (matchesActiveTopic) {
        fetchPosts(activeTopic.id)
      }

      if (matchesActiveTopic) {
        // Only auto-scroll if user hasn't manually scrolled up
        if (!userHasScrolled) {
          setTimeout(() => {
            scrollMessagesToBottom()
          }, 100)
        } else {
          // Show the scroll to bottom button if user has scrolled up
          setShowScrollToBottom(true)
        }
      }

      // Always refresh the forum topics to update message counts
      fetchForumTopics()
    },
    [dispatch, fetchPosts, activeTopic, fetchForumTopics, userHasScrolled, scrollMessagesToBottom],
  )

  const handleNewChat = useCallback(() => {
    fetchForumTopics()
  }, [fetchForumTopics])

  const handleDeletedMessage = useCallback(
    (deletedMessageId, topicId) => {
      if (activeTopic) {
        topicId = activeTopic.id
        console.log(`Message deleted: ${deletedMessageId} in topic: ${topicId}`)
        dispatch(deletePost(deletedMessageId))
        if (activeTopic && activeTopic.id === topicId) {
          fetchPosts(topicId)
        }
      }
    },
    [dispatch, activeTopic, fetchPosts],
  )

  useEffect(() => {
    let isMounted = true
    const unsubscribeState = signalRService.onStateChange((state) => {
      if (isMounted) setIsSignalRConnected(state === "connected")
    })
    const initializeSignalR = async () => {
      if (isAuthenticated) {
        try {
          await signalRService.start(() => getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
          }))
          if (isMounted) {
            signalRService.onChatMessage(handleReceiveMessage)
            signalRService.onNewChat(handleNewChat)
            signalRService.onDeleteChatMessage(handleDeletedMessage)
          }
        } catch (error) {
          if (isMounted) {
            setIsSignalRConnected(false)
          }
        }
      } else await signalRService.stop()
    }

    initializeSignalR()

    return () => {
      isMounted = false
      unsubscribeState()
      signalRService.offChatMessage(handleReceiveMessage)
      signalRService.offNewChat(handleNewChat)
      signalRService.offDeleteChatMessage(handleDeletedMessage)
    }
  }, [isAuthenticated, authLoading, getAccessTokenSilently, handleReceiveMessage, handleNewChat, handleDeletedMessage])

  // Replace the fetchChatParticipants function with this more efficient version
  const fetchUserHandles = useCallback(
    async (posts) => {
      if (!posts || posts.length === 0) return

      try {
        const uniqueUserIds = [...new Set(posts.map((post) => post.userId))]
        console.log(`Fetching handles for ${uniqueUserIds.length} unique users`)

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/GetUserHandles`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(uniqueUserIds),
        })

        if (!response.ok) {
          console.error("Failed to fetch user handles:", response.status)
          return
        }

        const userHandles = await response.json()

        const participantMap = Array.isArray(userHandles)
          ? userHandles.reduce((acc, handle) => {
              acc[handle.userId] = { name: handle.userHandle, avatarUrl: handle.profilePictureUrl }
              return acc
            }, {})
          : {}

        console.log("User handles fetched:", participantMap)
        setParticipants(participantMap)
      } catch (error) {
        console.error("Error fetching user handles:", error)
      }
    },
    [getAccessTokenSilently],
  )

  // 2. Add state for reply functionality and likes
  const [replyingTo, setReplyingTo] = useState(null)
  const [likedPosts, setLikedPosts] = useState({})

  // 3. Add function to handle replying to a post
  const handleReplyToPost = useCallback((post) => {
    setReplyingTo(post)
    // Focus on the message input
    setTimeout(() => {
      const messageInput = document.querySelector(".message-input")
      if (messageInput) {
        messageInput.focus()
      }
    }, 100)
  }, [])

  // 4. Add function to cancel reply
  const cancelReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  const handleImageSelect = useCallback((event) => {
    const file = event.target.files?.[0]
    if (!file) {
      setSelectedImage(null)
      setSelectedImageName("")
      return
    }

    const maxSizeBytes = 10 * 1024 * 1024
    if (file.size > maxSizeBytes) {
      setErrorState("Images must be 10 MB or smaller before upload.")
      setSelectedImage(null)
      setSelectedImageName("")
      return
    }

    setErrorState(null)
    setSelectedImage(file)
    setSelectedImageName(file.name)
  }, [])

  const clearSelectedImage = useCallback(() => {
    setSelectedImage(null)
    setSelectedImageName("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  // Ensure posts reset when the selected topic changes to avoid stale messages
  useEffect(() => {
    setPosts([])
    setReplyingTo(null)
    clearSelectedImage()
    setLikedPosts({})
    setExpandedThreads({})

    if (!activeTopic) return

    console.log("Selected topic changed, fetching posts for:", activeTopic.id)
    fetchPosts(activeTopic.id)

    // Reset user scroll state when changing topics
    setUserHasScrolled(false)
    setShowScrollToBottom(false)
  }, [activeTopic, fetchPosts, setPosts, clearSelectedImage])

  // 5. Modify handleSendMessage to include AtPostId when replying
  const handleSendMessage = useCallback(async () => {
    if (isSendingMessage || (!newMessage.trim() && !selectedImage) || !activeTopic) return

    try {
      setIsSendingMessage(true)
      await sendMessage(activeTopic.id, newMessage, replyingTo?.id, replyingTo?.userId, selectedImage)
      setNewMessage("")
      setReplyingTo(null)
      clearSelectedImage()

      // Reset user scroll state when sending a message
      setUserHasScrolled(false)
      setShowScrollToBottom(false)

      // Scroll to bottom after sending a message
      setTimeout(() => {
        scrollMessagesToBottom()
      }, 100)
    } catch (error) {
      setErrorState("Failed to send message.")
      dispatch(setError("Failed to send message."))
    } finally {
      setIsSendingMessage(false)
    }
  }, [
    activeTopic,
    newMessage,
    sendMessage,
    dispatch,
    replyingTo,
    selectedImage,
    clearSelectedImage,
    scrollMessagesToBottom,
    isSendingMessage,
  ])

  // 6. Add function to handle liking a post
  const handleLikePost = useCallback(
    async (postId) => {
      setLikedPosts((prev) => ({
        ...prev,
        [postId]: !prev[postId],
      }))

      const isLike = !likedPosts[postId]

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chats/like/${postId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isLike }),
        })

        if (!response.ok) {
          console.error("Failed to like post:", response.status)
          setLikedPosts((prev) => ({
            ...prev,
            [postId]: !prev[postId],
          }))
          return
        }

        const result = await response.json()
        if (typeof result?.likes === "number") {
          setPosts((prevPosts) =>
            Array.isArray(prevPosts)
              ? prevPosts.map((post) => (post.id === postId ? { ...post, likes: result.likes } : post))
              : prevPosts,
          )
        } else if (activeTopic) {
          fetchPosts(activeTopic.id)
        }
      } catch (error) {
        console.error("Error liking post:", error)
        setLikedPosts((prev) => ({
          ...prev,
          [postId]: !prev[postId],
        }))
      }
    },
    [activeTopic, fetchPosts, getAccessTokenSilently, likedPosts, setPosts],
  )

  const handleCategorySelect = useCallback(
    (category) => {
      setSelectedCategory(category)
      setIsTopicsCollapsed(false)
      if (isMobile) {
        setIsCategoryMenuOpen(false)
      }
      setLoading(true)
      fetchForumTopics()
    },
    [fetchForumTopics, isMobile],
  )

  const handleTopicClick = useCallback(
    (topic) => {
      dispatch(setSelectedForumTopic(topic))
      updateTopicUrl(topic.id)
      fetchPosts(topic.id)
      setIsTopicsCollapsed(true)
    },
    [dispatch, fetchPosts, updateTopicUrl],
  )

  const handleShareTopic = useCallback(
    (event, topic) => {
      event?.stopPropagation?.()
      if (!topic?.id) return
      copyTopicLink(topic.id)
    },
    [copyTopicLink],
  )

  const handleDeleteMessage = useCallback(async (messageId) => {
    setMessageToDelete(messageId)
    setShowDeleteConfirmation(true)
  }, [])

  const confirmDeleteMessage = useCallback(async () => {
    if (!messageToDelete) return
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chats/${messageToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        dispatch(deletePost(messageToDelete))
      } else {
        dispatch(setError("Failed to delete message."))
      }
    } catch (error) {
      console.error("An error occurred while deleting the message", error)
      dispatch(setError("An error occurred while deleting the message."))
    } finally {
      setShowDeleteConfirmation(false)
      setMessageToDelete(null)
    }
  }, [messageToDelete, dispatch, getAccessTokenSilently])

  const renderTopicListItem = useCallback(
    (topic, categoryMeta) => {
      const topicMessageCount =
        topic.messageCount ?? topic.postCount ?? topic.messages?.length ?? topic.messagesCount ?? 0
      const accentColor = categoryMeta?.color ?? "#6c757d"
      const categoryLabel = categoryMeta?.value ?? topic.category ?? UNCATEGORIZED_CATEGORY
      const categoryStyle = {
        borderColor: accentColor,
        color: accentColor,
        backgroundColor: `${accentColor}1a`,
      }

      return (
        <ListGroup.Item
          key={topic.id}
          action
          onClick={() => handleTopicClick(topic)}
          active={activeTopic?.id === topic.id}
          className="topic-item"
        >
          <div className="d-flex justify-content-between align-items-center w-100 topic-title-row">
            <div className="topic-title-group">
              <span className="topic-title">{topic.title}</span>
              {isGlobalForum && (
                <span className="topic-category-pill" style={categoryStyle}>
                  {categoryLabel}
                </span>
              )}
            </div>
            <div className="topic-actions">
              <span className="topic-post-count">
                {topicMessageCount} {topicMessageCount === 1 ? "post" : "posts"}
              </span>
              <Button
                variant="link"
                className="topic-share-button"
                onClick={(event) => handleShareTopic(event, topic)}
                title={copiedTopicId === topic.id ? "Link copied" : "Copy topic link"}
                aria-label={copiedTopicId === topic.id ? "Topic link copied" : "Copy topic link"}
              >
                <Link2 size={16} />
                {copiedTopicId === topic.id && <span className="topic-share-label">Copied</span>}
              </Button>
            </div>
          </div>
        </ListGroup.Item>
      )
    },
    [activeTopic, copiedTopicId, handleShareTopic, handleTopicClick, isGlobalForum],
  )

  const handleCreateTopic = async () => {
    if (!newTopicTitle.trim() || (isGlobalForum && !newTopicCategory)) return
    const hasGroupScope = groupId !== undefined && groupId !== null
    const effectiveGroupId = hasGroupScope ? groupId : null
    const sanitizedUserId = user?.sub?.replaceAll("|", "").replaceAll("-", "")

    try {
      // Create a new topic with isChat=false for forums using Topics controller
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Topics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_API_AUDIENCE,
            },
          })}`,
        },
        body: JSON.stringify({
          title: newTopicTitle,
          isChat: false,
          groupId: effectiveGroupId,
          userId: sanitizedUserId,
          public: !hasGroupScope,
          createdOn: new Date().toISOString(),
          category: isGlobalForum ? newTopicCategory : null,
          isDeleted: false,
          isFlagged: false,
        }),
      })

      if (response.ok) {
        const newTopic = await response.json()
        const normalizedId =
          newTopic.id ??
          newTopic.topicId ??
          newTopic.TopicId ??
          newTopic.chatId ??
          newTopic.ChatId ??
          newTopic.topicID ??
          newTopic.TopicID
        const normalizedNewTopic = {
          ...newTopic,
          id: normalizedId,
          category: isGlobalForum ? (newTopic.category ?? newTopic.Category ?? newTopicCategory) : null,
          createdOn: newTopic.createdOn ?? new Date().toISOString(),
        }
        setForumTopics((prevTopics) => [normalizedNewTopic, ...prevTopics])
        setNewTopicTitle("")
        setNewTopicCategory(TOPIC_CATEGORIES[0].value)
        setShowNewTopicModal(false)
        if (isGlobalForum) {
          setSelectedCategory(newTopicCategory)
        }

        // Refresh topics to ensure we have the DB-assigned ID
        const refreshedTopics = await fetchForumTopics()
        const resolvedTopic =
          refreshedTopics?.find((topic) => topic.id && topic.title === normalizedNewTopic.title) ||
          refreshedTopics?.find((topic) => topic.id === normalizedId) ||
          normalizedNewTopic

        // Select the new topic with its resolved id
        dispatch(setSelectedForumTopic(resolvedTopic))
        if (resolvedTopic?.id) {
          updateTopicUrl(resolvedTopic.id)
          fetchPosts(resolvedTopic.id)
        }
        setIsTopicsCollapsed(true)
      } else {
        setErrorState("Failed to create topic.")
      }
    } catch (error) {
      console.error("Failed to create topic:", error)
      setErrorState("Failed to create topic.")
    }
  }

  // Get user initials for avatar
  const getUserInitials = (username) => {
    if (!username) return "?"
    return username
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2)
  }

  // Normalize ID for comparison
  const normalizeId = useCallback((id) => id?.replace(/[-|]/g, ""), [])

  const getDisplayName = useCallback(
    (userId) => {
      const fullName = participants[userId]?.name
      if (!fullName) return "Unknown User"
      const parts = fullName.trim().split(/\s+/)
      if (parts.length === 1) return parts[0]
      const firstName = parts[0]
      const lastInitial = parts[parts.length - 1]?.[0]?.toUpperCase() ?? ""
      return lastInitial ? `${firstName} ${lastInitial}.` : firstName
    },
    [participants],
  )

  const toggleReplies = useCallback((postId) => {
    setExpandedThreads((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }))
  }, [])

  const renderMessageWithReplies = (message, depth = 0) => {
    const isOwnMessage = normalizeId(message.userId) === normalizeId(user?.sub)
    const messageDate = message.postDate ? new Date(message.postDate) : message.createdOn ? new Date(message.createdOn) : null
    const formattedDate = messageDate && !isNaN(messageDate) ? messageDate.toLocaleString() : "Unknown date"
    const replies = repliesByParent[message.id] || []
    const parentMessage = message.atPostId ? visiblePosts.find((p) => p.id === message.atPostId) : null

    const participant = participants[message.userId]
    const messageDisplayName = getDisplayName(message.userId)
    const parentDisplayName = parentMessage ? getDisplayName(parentMessage.userId) : null
    const avatarInitials = getUserInitials(participant?.name || messageDisplayName || "Unknown User")

    return (
      <div
        key={`${message.id}-${depth}`}
        ref={(node) => registerMessageRef(message.id, node)}
        className={`message ${isOwnMessage ? "message-own" : "message-other"} ${depth > 0 ? "message-nested" : ""}`}
      >
        <div className="message-header">
          <div className="message-username">
            <div className="message-avatar">
              {avatarInitials}
            </div>
            {messageDisplayName}
          </div>
        </div>

        {parentMessage && (
          <div className="replied-to-message">
            <div className="replied-to-content">
              <small>
                <em>Replying to @{parentDisplayName || "Unknown User"}:</em>
                {parentMessage.body.length > 50 ? parentMessage.body.substring(0, 50) + "..." : parentMessage.body}
              </small>
            </div>
          </div>
        )}

        <div className="message-content">{message.body}</div>
        {message.imageUrl && (
          <div className="message-image-wrapper">
            <img src={message.imageUrl} alt="Uploaded attachment" className="message-image" loading="lazy" />
          </div>
        )}

        <div className="message-footer">
          <div className="message-timestamp">
            <Clock size={12} className="me-1" />
            {formattedDate}
          </div>

          <div className="message-actions">
            <Button
              variant="link"
              className="action-button reply-button"
              onClick={() => handleReplyToPost(message)}
              title="Reply to this message"
            >
              <MessageCircle size={14} />
            </Button>

            <Button
              variant="link"
              className={`action-button like-button ${likedPosts[message.id] ? "liked" : ""}`}
              onClick={() => handleLikePost(message.id)}
              title={likedPosts[message.id] ? "Unlike this message" : "Like this message"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill={likedPosts[message.id] ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
              {message.likes > 0 && <span className="like-count">{message.likes}</span>}
            </Button>

            {isOwnMessage && (
              <Button
                variant="link"
                className="action-button delete-button"
                onClick={() => handleDeleteMessage(message.id)}
                title="Delete this message"
              >
                <span role="img" aria-label="Delete">
                  🗑️
                </span>
              </Button>
            )}
          </div>
        </div>

        {replies.length > 0 && (
          <div className="message-replies">
            {!expandedThreads[message.id] ? (
              <Button variant="link" className="show-replies-button" onClick={() => toggleReplies(message.id)}>
                Show replies ({replies.length})
              </Button>
            ) : (
              <>
                <Button variant="link" className="show-replies-button" onClick={() => toggleReplies(message.id)}>
                  Hide replies
                </Button>
                {replies.map((reply) => renderMessageWithReplies(reply, depth + 1))}
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  // Toggle topics sidebar
  const toggleTopics = useCallback(() => {
    setIsTopicsCollapsed(!isTopicsCollapsed)
  }, [isTopicsCollapsed])

  useEffect(() => {
    setIsCategoryMenuOpen(!isMobile)
  }, [isMobile])

  // Replace the useEffect that calls fetchChatParticipants with this
  useEffect(() => {
    if (activeTopic && visiblePosts.length > 0) {
      fetchUserHandles(visiblePosts) // ✅ MINIMAL CHANGE: posts -> postsArray
    }
  }, [activeTopic, visiblePosts, fetchUserHandles]) // ✅ MINIMAL CHANGE: posts -> postsArray

  if (authLoading) {
    return (
      <Card className={`forum-card ${isDarkMode ? "dark" : ""}`}>
        <Card.Body className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading forums...</p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (!isAuthenticated) {
    return (
      <Card className={`forum-card ${isDarkMode ? "dark" : ""}`}>
        <Card.Body className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="text-center">
            <AlertCircle size={48} className="text-muted mb-3" />
            <h5>Authentication Required</h5>
            <p className="text-muted">Please log in to access the forums.</p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const containerClasses = [
    "forum-container",
    isGroupForum ? "group-forum-mode" : "global-forum-mode",
    isTopicsCollapsed ? "topics-collapsed" : "topics-expanded",
    isMobile && !isTopicsCollapsed ? "mobile-full-topics" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={isDarkMode ? "dark" : ""}>
      <Card className={`forum-card ${isGroupForum ? "group-forum-card" : "global-forum-card"}`}>
        <div className="forum-header">
          <div>
            <span className="forum-eyebrow">{isGroupForum ? "Member conversation" : "Community-wide conversation"}</span>
            <h4 className="forum-title mb-0">{forumTitle}</h4>
          </div>
          <div className="connection-status">
            <div
              className={`status-indicator ${isSignalRConnected ? "status-connected" : "status-disconnected"}`}
            ></div>
            <span>{isSignalRConnected ? "Connected" : "Disconnected"}</span>
          </div>
        </div>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <div className={containerClasses}>
            <div className={`topics-list p-3 ${isTopicsCollapsed ? "collapsed" : ""}`}>
              <div className="topics-header">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 me-3">Select a topic</h5>
                    <Button variant="primary" className="new-topic-button" onClick={() => setShowNewTopicModal(true)}>
                      <Plus size={16} className="me-1" /> New Topic
                    </Button>
                  </div>
                </div>
              </div>

              {isGlobalForum && (
                <div className="category-section">
                  {isMobile && (
                    <button
                      type="button"
                      className="category-toggle"
                      onClick={() => setIsCategoryMenuOpen((prev) => !prev)}
                      aria-expanded={isCategoryMenuOpen}
                      aria-controls="category-tabs-list"
                    >
                      <div className="category-toggle-labels">
                        <span className="category-toggle-title">Category</span>
                        <span className="category-toggle-selected">{selectedCategory}</span>
                      </div>
                      {isCategoryMenuOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  )}

                  {(!isMobile || isCategoryMenuOpen) && (
                    <div className="category-tabs" role="tablist" aria-label="Forum categories" id="category-tabs-list">
                      {CATEGORY_TABS.map((category) => {
                        const isActive = selectedCategory === category
                        const categoryDetails =
                          category === ALL_CATEGORIES_TAB
                            ? { color: "#0ea5e9" }
                            : category === UNCATEGORIZED_CATEGORY
                              ? { color: "#6c757d" }
                              : getCategoryDetails(category)

                        return (
                          <button
                            key={category}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-label={`Filter by ${category} topics`}
                            className={`category-tab ${isActive ? "active" : ""}`}
                            style={{ borderColor: isActive ? categoryDetails.color : undefined }}
                            onClick={() => handleCategorySelect(category)}
                          >
                            <span className="category-tab-dot" style={{ backgroundColor: categoryDetails.color }} />
                            <span className="category-tab-label">{category}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="topics-meta">
                <small>
                  Showing {displayedTopics.length} of {filteredTopics.length} topics
                  {filteredTopics.length > TOPICS_PAGE_SIZE ? ` (first ${TOPICS_PAGE_SIZE})` : ""}
                  {isGlobalForum
                    ? ` in the ${selectedCategory === ALL_CATEGORIES_TAB ? "all categories" : selectedCategory} tab.`
                    : " for this group."}
                </small>
              </div>

              {loading ? (
                <div className="loading-container topics-loading">
                  <Loader2 className="topics-loading-icon spin" aria-hidden="true" />
                  <div className="topics-loading-text">Topics loading...</div>
                </div>
              ) : displayedTopics.length === 0 ? (
                <div className="empty-state">
                  <MessageCircle size={32} />
                  <p className="empty-state-text">No topics yet</p>
                  <p className="empty-state-subtext">Create a new topic to start a discussion</p>
                </div>
              ) : (
                <ListGroup className="w-100">
                  {displayedTopics.map((topic) => renderTopicListItem(topic, getCategoryDetails(topic.category)))}
                </ListGroup>
              )}
            </div>

            {hasActiveTopic ? (
              <div className="messages-container">
                <div className={`messages-header ${isTopicsCollapsed ? "has-back-button" : ""}`}>
                  {isTopicsCollapsed && (
                    <Button
                      variant="outline-secondary"
                      className="back-to-topics-button"
                      onClick={toggleTopics}
                      aria-label="Back to topics"
                    >
                      <ChevronLeft size={18} />
                      <span>Back to topics</span>
                    </Button>
                  )}
                  <h2 className="messages-title">{activeTopic?.title}</h2>
                  <div className="messages-actions">
                    <Button
                      variant="outline-secondary"
                      className="messages-share-button"
                      onClick={(event) => handleShareTopic(event, activeTopic)}
                      title={copiedTopicId === activeTopic?.id ? "Link copied" : "Copy topic link"}
                      aria-label={copiedTopicId === activeTopic?.id ? "Topic link copied" : "Copy topic link"}
                    >
                      <Link2 size={16} />
                      <span className="messages-share-label">
                        {copiedTopicId === activeTopic?.id ? "Link copied" : "Copy link"}
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="messages-list" ref={chatMessagesRef} onScroll={handleMessagesScroll}>
                  {loading ? (
                    <div className="loading-container">
                      <Spinner animation="border" size="sm" variant="primary" />
                    </div>
                  ) : visiblePosts.length === 0 ? ( // ✅ MINIMAL CHANGE
                    <div className="empty-state">
                      <MessageCircle size={32} />
                      <p className="empty-state-text">No messages yet</p>
                      <p className="empty-state-subtext">Be the first to post in this topic!</p>
                    </div>
                  ) : (
                    rootPosts.map((message) => renderMessageWithReplies(message))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {showScrollToBottom && (
                  <Button
                    variant="primary"
                    className="scroll-to-bottom-button"
                    onClick={scrollToBottom}
                    aria-label="Scroll to bottom"
                  >
                    <ArrowDown size={16} />
                  </Button>
                )}

                <div className="message-input-container">
                  {replyingTo && (
                    <div className="replying-to-container">
                      <div className="replying-to-info">
                        <small>
                          <em>Replying to @{getDisplayName(replyingTo.userId)}:</em>
                          {replyingTo.body.length > 50 ? replyingTo.body.substring(0, 50) + "..." : replyingTo.body}
                        </small>
                      </div>
                      <Button variant="link" className="cancel-reply-button" onClick={cancelReply} title="Cancel reply">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </Button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleImageSelect}
                  />
                  {selectedImageName && (
                    <div className="attached-image-chip">
                      <span className="attached-image-name" title={selectedImageName}>
                        {selectedImageName}
                      </span>
                      <Button
                        variant="link"
                        className="clear-image-button"
                        onClick={clearSelectedImage}
                        title="Remove attached image"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                  <InputGroup className="message-input-group">
                    <Form.Control
                      as="textarea"
                      rows={1}
                      placeholder="Type your message..."
                      value={newMessage}
                      ref={messageInputRef}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault()
                          if (e.repeat) return
                          handleSendMessage()
                        }
                      }}
                      className="message-input"
                    />
                    <div className="emoji-picker">
                      <Button
                        variant="outline-secondary"
                        className="emoji-picker__toggle"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        aria-label="Add emoji"
                      >
                        <Smile size={18} />
                      </Button>
                      {showEmojiPicker && (
                        <div className="emoji-picker__panel" role="listbox" aria-label="Emoji list">
                          {EMOJI_OPTIONS.map((emoji) => (
                            <button
                              type="button"
                              key={`forum-emoji-${emoji}`}
                              className="emoji-picker__option"
                              onClick={() => {
                                insertEmoji(emoji)
                                setShowEmojiPicker(false)
                              }}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      className="attach-button"
                      title="Attach an image"
                    >
                      <Paperclip size={16} />
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSendMessage}
                      className="send-button"
                      aria-label="Send message"
                      title="Send message"
                    >
                      <Send size={16} aria-hidden="true" />
                    </Button>
                  </InputGroup>
                </div>
              </div>
            ) : (
              <div className="messages-container">
                <div className="empty-state">
                  <Users size={48} />
                  <p className="empty-state-text">No topic selected</p>
                  <p className="empty-state-subtext">Choose a topic from the list to view messages and start chatting.</p>
                </div>
              </div>
            )}
          </div>
        </Card.Body>

        <Modal
          show={showNewTopicModal}
          onHide={() => setShowNewTopicModal(false)}
          centered
          dialogClassName="new-topic-modal"
          contentClassName={`new-topic-modal-content ${isDarkMode ? "dark" : ""}`}
        >
          <Modal.Header closeButton>
            <Modal.Title>Create New Topic</Modal.Title>
          </Modal.Header>
          <Modal.Body className="new-topic-modal-body">
            <Form.Group className="new-topic-form-group">
              <Form.Label>Topic Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter topic title"
                value={newTopicTitle}
                onChange={(e) => setNewTopicTitle(e.target.value)}
                className="new-topic-input"
              />
              <Form.Text className="new-topic-helper">Give your discussion a clear, inviting headline.</Form.Text>
            </Form.Group>
            {isGlobalForum && (
              <Form.Group className="mt-3 new-topic-form-group">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={newTopicCategory}
                  onChange={(e) => setNewTopicCategory(e.target.value)}
                  className="new-topic-select"
                >
                  {TOPIC_CATEGORIES.map((categoryOption) => (
                    <option key={categoryOption.value} value={categoryOption.value}>
                      {categoryOption.value}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="new-topic-helper">Choose where this conversation best fits.</Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowNewTopicModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateTopic} disabled={!newTopicTitle.trim() || (isGlobalForum && !newTopicCategory)}>
              Create Topic
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showDeleteConfirmation}
          onHide={() => setShowDeleteConfirmation(false)}
          centered
          contentClassName={`delete-confirm-modal ${isDarkMode ? "dark" : ""}`}
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete message</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="delete-confirm-icon" aria-hidden="true">
              🗑️
            </div>
            <p className="delete-confirm-text">Are you sure you want to delete?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" className="delete-confirm-cancel" onClick={() => setShowDeleteConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="delete-confirm-delete" onClick={confirmDeleteMessage}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Card>
    </div>
  )
}

export default GroupForums
